'use client';

import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduledDayColumn } from './scheduled-day-column.component';
import { ScheduledDaysHeader } from './scheduled-days-header.component';

import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { ScheduleSlotsDay } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { cn } from '@/utils/cn-tw-merge';

const CARD_SLOTS_PER_COLUMN = 4;

interface ScheduledSlotsGridProps {
  days: ScheduleSlotsDay[];
  markedSlotIds: ReadonlySet<string>;
  busySlotIds: ReadonlySet<string>;
  hasMore: boolean;
  offset: number;
  className?: string;
  slotsPerColumn?: number;
  compact?: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSeeMore: () => void;
  onSelectSlot: (slot: SchedulesSearchSlot) => void;
}

export function ScheduledSlotsGrid({
  days,
  markedSlotIds,
  busySlotIds,
  hasMore,
  offset,
  className,
  slotsPerColumn = CARD_SLOTS_PER_COLUMN,
  compact = false,
  onPrev,
  onNext,
  onSeeMore,
  onSelectSlot,
}: ScheduledSlotsGridProps) {
  const maxSlotsInAnyDay = days.reduce((max, day) => Math.max(max, day.slots.length), 0);
  const canSeeMore = compact || maxSlotsInAnyDay > slotsPerColumn;

  if (days.length === 0) {
    return (
      <div
        className={cn('flex flex-col gap-4 w-full', className)}
        data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_GRID}
      >
        {(offset > 0 || hasMore) && (
          <ScheduledDaysHeader
            days={days}
            prevDisabled={offset === 0}
            nextDisabled={!hasMore}
            compact={compact}
            onPrev={onPrev}
            onNext={onNext}
          />
        )}
        <Typography variant={TypographyVariant.BODY_TEXT} className="w-full text-center text-gray-500 py-6">
          Nenhum horário disponível.
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-col gap-4 w-full', className)}
      data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_GRID}
    >
      <div className="flex flex-col gap-2 w-full">
        <ScheduledDaysHeader
          days={days}
          prevDisabled={offset === 0}
          nextDisabled={!hasMore}
          compact={compact}
          onPrev={onPrev}
          onNext={onNext}
        />
        <div className="flex items-start justify-center w-full">
          <div className={cn('shrink-0', compact ? 'w-6 xl:w-8' : 'w-8')} />
          <div className="flex-1 flex items-start gap-4 min-w-0">
            {days.map((day) => (
              <ScheduledDayColumn
                key={day.date}
                slots={day.slots}
                visibleSlotsLimit={slotsPerColumn}
                slotClassName={compact ? 'py-2 text-base' : undefined}
                markedSlotIds={markedSlotIds}
                busySlotIds={busySlotIds}
                onSelectSlot={onSelectSlot}
              />
            ))}
          </div>
          <div className={cn('shrink-0', compact ? 'w-6 xl:w-8' : 'w-8')} />
        </div>
      </div>
      {canSeeMore && (
        <Button
          variant="text"
          color="primary"
          startIcon={faPlus}
          startIconSize="xs"
          startIconClassName="text-xs text-green-500"
          className="self-center gap-2 px-4 py-2"
          data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_SEE_MORE}
          onClick={onSeeMore}
        >
          Ver outros horários
        </Button>
      )}
    </div>
  );
}
